// Layer 4 (UI) — planned vs unplanned downtime, by month and for the YTD
// window (rebuilt to the locked comp `downtime.html` A). Every figure comes
// from PlannedRatio.transform.ts; nothing is typed here.

import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { KpiCard, MeterBar } from '@/versions/v1/modules/_viz';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { ActorChip } from '../advisory/PipelineStrip';
import { KpiStrip } from '../maintenance/KpiStrip';
import { downtimeEvents } from '../maintenance/DowntimeEvents.transform';
import { formatHours } from '../maintenance/Downtime.transform';
import { CARD_TABLE_DENSE, DowntimeCard, SYN_CHIP, THREE_UP } from './DowntimeCard';
import { plannedRatioMonths, plannedRatioRead } from './PlannedRatio.transform';

const BAR_TRACK = 'flex h-3 w-full overflow-hidden rounded-[3px] bg-[var(--hairline)]';

export function PlannedRatioTab() {
  const events = downtimeEvents();
  const read = plannedRatioRead(events);
  const months = plannedRatioMonths(events);
  const maxMonthlyHours = Math.max(1, ...months.map((row) => row.total));

  return (
    <Commentable name="PlannedRatio">
      <div className="space-y-3">
        <div className={THREE_UP}>
          <KpiStrip>
            <KpiCard
              label="YTD planned ratio"
              value={`${Math.round(read.plannedPct)}%`}
              sub={`${read.windowLabel} · ${read.targetLabel.toLowerCase()}`}
              tone={read.tone}
              className="px-3.5 py-3"
              footer={
                <MeterBar
                  className="w-full"
                  value={read.plannedPct}
                  max={100}
                  marker={{ value: read.targetPct, label: read.targetLabel }}
                  markerCaption={false}
                  markerInTrack
                  tone={read.tone}
                />
              }
            />
            <KpiCard label="Total planned hours" value={formatHours(read.planned)} sub={read.windowLabel} tone="ok" className="px-3.5 py-3" />
            <KpiCard label="Total unplanned hours" value={formatHours(read.unplanned)} sub={read.windowLabel} tone="bad" className="px-3.5 py-3" />
          </KpiStrip>
        </div>

        <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-start">
          <DowntimeCard
            title="Monthly planned vs unplanned hours"
            meta={read.windowLabel}
            actions={SYN_CHIP}
            className="xl:col-span-7"
          >
            <div className="space-y-2.5">
              {months.map((row) => (
                <div key={row.key} className="grid grid-cols-[2.75rem_minmax(0,1fr)_7.5rem] items-center gap-3 text-sm" title={row.title}>
                  <span className="font-medium text-ink-primary">{row.label}</span>
                  <span className={BAR_TRACK}>
                    <span className="flex h-full" style={{ width: `${(row.total / maxMonthlyHours) * 100}%` }}>
                      <span className="h-full bg-[var(--state-online)]" style={{ width: `${row.plannedSharePct}%` }} />
                      <span className="h-full bg-[var(--state-blocked)]" style={{ width: `${row.unplannedSharePct}%` }} />
                    </span>
                  </span>
                  <span className="text-right text-xs tabular-nums text-ink-secondary">
                    {Math.round(row.plannedPct)}% · {formatHours(row.total)}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3.5 text-xs text-muted">
              <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-[3px] bg-[var(--state-online)]" />Planned</span>
              <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-[3px] bg-[var(--state-blocked)]" />Unplanned</span>
              <span>{read.targetLabel} planned</span>
            </div>
          </DowntimeCard>

          <DowntimeCard title="Monthly downtime" actions={SYN_CHIP} className="xl:col-span-5">
            <div className={CARD_TABLE_DENSE}>
              <Table wrap={false} className="tabular-nums">
                <THead><TR><TH>Month</TH><TH className="text-right">Planned</TH><TH className="text-right">Unplanned</TH><TH className="text-right">Total</TH><TH className="text-right">Planned ratio</TH></TR></THead>
                <TBody>
                  {months.map((row) => (
                    <TR key={row.key} title={row.title}>
                      <TD className="font-medium !text-ink-primary">{row.label}</TD>
                      <TD className="text-right">{formatHours(row.planned)}</TD>
                      <TD className="text-right">{formatHours(row.unplanned)}</TD>
                      <TD className="text-right">{formatHours(row.total)}</TD>
                      <TD className="text-right font-semibold !text-ink-primary">{Math.round(row.plannedPct)}%</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </DowntimeCard>
        </div>

        <DowntimeCard aria-label="AI suggestion" className="py-2.5">
          <div className={cn('flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm')}>
            <span className="flex items-baseline gap-2 text-ink-primary">
              <ActorChip actor="Engine">AI suggestion</ActorChip>
              Unplanned hours lead planned hours by {read.unplannedToPlanned.toFixed(1)}×
            </span>
            <span className="text-xs tabular-nums text-muted">{read.unplanned.toFixed(1)} h ÷ {read.planned.toFixed(1)} h · {read.windowLabel}</span>
          </div>
        </DowntimeCard>
      </div>
    </Commentable>
  );
}
